"use client";

import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface OptionType {
  key: string;
  value: string;
}

interface CountrySearchComboboxProps {
  options: OptionType[] | string[];
  /**
   * Optional: List of popular (featured) options to show at the top in a separate group.
   */
  popularOptions?: OptionType[] | string[];
  selected: string[];
  setSelected: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  label?: string;
  singleSelect?: boolean;
}

export const CountrySearchCombobox: React.FC<CountrySearchComboboxProps> = ({
  options,
  popularOptions = [],
  selected,
  setSelected,
  placeholder,
  className,
  disabled = false,
  label,
  singleSelect = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const safeSelected = Array.isArray(selected) ? selected : [];

  // Normalize options to always be in OptionType format
  const normalizeOptions = (items: OptionType[] | string[]): OptionType[] => {
    if (!items || !items.length) return [];
    return items.map(item => 
      typeof item === 'string' ? { key: item, value: item } : item
    );
  };

  const normalizedOptions = React.useMemo(() => normalizeOptions(options), [options]);
  const normalizedPopularOptions = React.useMemo(() => normalizeOptions(popularOptions || []), [popularOptions]);

  const handleSelect = React.useCallback(
    (countryValue: string) => {
      // Find the actual value (in case we're dealing with OptionType objects)
      const selectedOption = normalizedOptions.find(opt => opt.key === countryValue) || 
                           normalizedPopularOptions.find(opt => opt.key === countryValue);
      
      const valueToUse = selectedOption?.value || countryValue;
      
      if (safeSelected.includes(valueToUse)) {
        setSelected(safeSelected.filter((v) => v !== valueToUse));
      } else {
        if (singleSelect) {
          setSelected([valueToUse]);
        } else {
          setSelected([...safeSelected, valueToUse]);
        }
      }
    },
    [safeSelected, setSelected, singleSelect, normalizedOptions, normalizedPopularOptions]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className={cn("w-full", className)}>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full min-w-0 justify-between"
            type="button"
            disabled={disabled}
          >
            {safeSelected.length > 0 ? (
              <div className="truncate">
                {safeSelected
                  .map(
                    (val) =>
                      options.find((opt) => opt.value === val)?.key || val
                  )
                  .join(", ")}
              </div>
            ) : (
              placeholder || "Select countries..."
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[--radix-popover-trigger-width] min-w-0 p-0"
      >
        <Command>
          <CommandInput placeholder="Search countries..." disabled={disabled} />
          <CommandList className="max-h-[400px] overflow-auto" onWheel={(e) => e.stopPropagation()}>
            <CommandEmpty>No countries found.</CommandEmpty>
            {/* Popular Countries group */}
            {normalizedPopularOptions.length > 0 && (
              <CommandGroup heading="Popular Countries">
                {normalizedPopularOptions.map(opt => {
                  const isSelected = safeSelected.includes(opt.value);
                  return (
                    <CommandItem
                      key={opt.value}
                      value={opt.key}
                      onSelect={() => {
                        handleSelect(opt.key);
                        if (singleSelect) setOpen(false);
                      }}
                      disabled={singleSelect && isSelected && !safeSelected.includes(opt.value)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          isSelected ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {opt.key}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
            {/* All countries group, excluding those already in popularOptions */}
            <CommandGroup heading="All Countries">
              {normalizedOptions
                .filter(opt => !normalizedPopularOptions.some(pop => pop.value === opt.value))
                .map((opt) => {
                  const isSelected = safeSelected.includes(opt.value);
                  return (
                    <CommandItem
                      key={opt.value}
                      value={opt.key}
                      onSelect={() => {
                        handleSelect(opt.key);
                        if (singleSelect) setOpen(false);
                      }}
                      disabled={singleSelect && isSelected && !safeSelected.includes(opt.value)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          isSelected ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {opt.key}
                    </CommandItem>
                  );
                })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default CountrySearchCombobox;
